import React, { useEffect } from "react";
import Routes from "./Routes";
import { SwirlModalProvider } from "./contexts/SwirlModalContext";
import { SettingsModalProvider } from "./contexts/SettingsModalContext";
import { RealtimeProvider } from "./contexts/RealtimeContentContext";
import { AskSwirlProvider } from "./contexts/AskSwirlContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { useAuth } from "./contexts/AuthContext";
import { setQueueRecoveryUserId } from "./utils/queueRecovery";
import ErrorBoundary from "./components/ErrorBoundary";
import ProcessingQueueStatus from "./components/ProcessingQueueStatus";
import DropZoneOverlay from "./components/upload/DropZoneOverlay";
import DragDropOverlay from "./components/upload/DragDropOverlay";
import { useIngestion } from "./hooks/useIngestion";
import { logActiveAPIs, FLAGS } from "./lib/flags";
import deepsearchService from "./services/deepsearchService";
import { runBackfillEmbeddings } from "./utils/embeddings";
import { OnboardingModal, useOnboarding } from "./features/onboarding";
import './scripts/backfillVault'; // Load vault backfill commands
import './scripts/testVaultPipeline'; // Load vault test suite
import './utils/vaultDebug'; // Load comprehensive vault debug tools
import './utils/clearCache'; // Load cache clear utility
import './utils/testDeepsearchAccess'; // Load deepsearch access test

function AppContent() {
  const { user } = useAuth();
  const { uploadFiles } = useIngestion();
  const { checkIfNewUser, openOnboarding } = useOnboarding();

  // Phase 9.6: Log active APIs at startup
  useEffect(() => {
    logActiveAPIs();
    // Debug: Log FLAGS to verify feature flags are loaded correctly
    if (import.meta.env.DEV) {
      console.log('🚩 Feature FLAGS:', 
        'SELF_DASHBOARD_ENABLED=' + FLAGS.SELF_DASHBOARD_ENABLED,
        'SWIRLSPACE_ENABLED=' + FLAGS.SWIRLSPACE_ENABLED,
        'DEEPSEARCH_ENABLED=' + FLAGS.DEEPSEARCH_ENABLED
      );
    }
  }, []);

  // Auto-launch onboarding for new users
  useEffect(() => {
    async function checkAndLaunchOnboarding() {
      if (!user?.id) return;

      const isNewUser = await checkIfNewUser(user.id);
      
      if (isNewUser) {
        // Wait 500ms after login for smooth UX
        setTimeout(() => {
          openOnboarding();
        }, 500);
      }
    }

    checkAndLaunchOnboarding();
  }, [user?.id, checkIfNewUser, openOnboarding]);

  // Expose backfill function to browser console
  useEffect(() => {
    window.runBackfill = runBackfillEmbeddings;
    if (import.meta.env.DEV) {
      console.log('🔧 Backfill function available: window.runBackfill()');
      console.log('🔧 Vault backfill available: window.backfillVault()');
      console.log('🔧 Vault validation available: window.validateVault()');
      console.log('🧪 Vault test suite available: window.testVaultPipeline()');
    }
  }, []);

  // Initialize queue recovery when user is available
  useEffect(() => {
    if (user?.id) {
      setQueueRecoveryUserId(user.id);
    }
  }, [user?.id]);

  // TEMPORARY: Run embedding accuracy test once at startup
  useEffect(() => {
    if (user?.id) {
      // Run test after a short delay to ensure auth is fully initialized
      const timer = setTimeout(() => {
        deepsearchService.debugEmbeddingTest().catch(err => {
          if (import.meta.env.DEV) console.warn('Embedding test failed:', err);
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user?.id]);

  // Handle drag & drop uploads
  const handleFilesDropped = async (files, onProgress) => {
    const result = await uploadFiles(files, onProgress);
    if (result.success) {
      console.log('✅ Files uploaded successfully!', result.results);
    } else {
      console.error('❌ Upload failed:', result.error);
    }
  };

  return (
    <>
      {/* New drag & drop overlay with cool animations */}
      <DragDropOverlay />
      
      {/* Legacy drop zone overlay (keep for backwards compatibility) */}
      <DropZoneOverlay 
        onFilesDropped={handleFilesDropped}
        enabled={!!user}
      />
      
      <Routes />
      <ProcessingQueueStatus />
      
      {/* Onboarding Modal */}
      <OnboardingModal />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <RealtimeProvider>
        <SidebarProvider>
          <AskSwirlProvider>
            <SwirlModalProvider>
              <SettingsModalProvider>
                <AppContent />
              </SettingsModalProvider>
            </SwirlModalProvider>
          </AskSwirlProvider>
        </SidebarProvider>
      </RealtimeProvider>
    </ErrorBoundary>
  );
}

export default App;